import React from 'react'

import { inject, observer } from 'mobx-react'

import { IRemoteTileset, ITileset, MapFileType } from '~/client/graph'
import Sitemap from '~/client/src/shared/models/Sitemap'
import { SAVE_GLOBE_VIEW } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import { isPlanDrawable, mapFileKey } from '../../../MapBoxViewer/globeMapFiles'
import MapBoxViewerStore, {
  ISitemapWithBasemap,
} from '../../../MapBoxViewer/MapBoxViewer.store'
import GlobeViewPlan from './GlobeViewPlan'
import GlobeViewRemoteTileset from './GlobeViewRemoteTileset'
import GlobeViewTileset from './GlobeViewTileset'
import UnderGlobePlanShape from './UnderGlobePlanShape'

export type MapFileOnGlobe = {
  type: MapFileType
  file: ISitemapWithBasemap | ITileset | IRemoteTileset
}
interface IProps {
  mapBoxViewerStore: MapBoxViewerStore
  sitemapWithBasemaps: ISitemapWithBasemap[]
  mapFilesOnGlobe: MapFileOnGlobe[]
  // Keys (mapFileKey) of files kept mounted but not drawn, so draw order holds when they return.
  hiddenMapFiles?: ReadonlySet<string>
  selectedSitemap?: Sitemap
  isEditor?: boolean
  isRubberMode?: boolean
  eventsStore?: EventsStore
  className?: string
}

@inject('eventsStore')
@observer
export default class GlobeViewFiles extends React.Component<IProps> {
  public render(): JSX.Element {
    const {
      mapBoxViewerStore,
      sitemapWithBasemaps,
      isRubberMode,
      selectedSitemap,
      mapFilesOnGlobe,
      hiddenMapFiles,
      eventsStore,
    } = this.props
    const isGlobeLoading = eventsStore.appState.loading.get(SAVE_GLOBE_VIEW)
    if (isGlobeLoading) {
      return null
    }
    return (
      <>
        {this.props?.isEditor && (
          <UnderGlobePlanShape
            mapBoxViewerStore={mapBoxViewerStore}
            sitemapWithBasemaps={sitemapWithBasemaps}
            isRubberMode={isRubberMode}
            selectedSitemap={selectedSitemap}
          />
        )}
        {mapFilesOnGlobe.map((f, idx) => {
          const isHidden = !!hiddenMapFiles?.has(mapFileKey(f))
          switch (f.type) {
            case MapFileType.Sitemap:
              return this.renderGlobePlan(
                f.file as ISitemapWithBasemap,
                idx,
                isHidden,
              )
            case MapFileType.Tileset:
              return this.renderGlobeTileset(f.file as ITileset, idx, isHidden)
            case MapFileType.RemoteTileset:
              return this.renderGlobeRemoteTileset(
                f.file as IRemoteTileset,
                idx,
                isHidden,
              )
            default:
              return null
          }
        })}
      </>
    )
  }

  private renderGlobePlan(
    sitemapWithBasemap: ISitemapWithBasemap,
    idx: number,
    isHidden: boolean,
  ): JSX.Element {
    const { sitemap } = sitemapWithBasemap
    const isVisible =
      !this.props.isRubberMode ||
      !!this.props.sitemapWithBasemaps.find(s => s.sitemap.id === sitemap.id)
    if (!isVisible || !isPlanDrawable(sitemapWithBasemap)) {
      return null
    }
    const { mapBoxViewerStore } = this.props
    return (
      <div key={'globe-plan-' + idx}>
        <GlobeViewPlan
          idx={idx}
          sitemapWithBasemap={sitemapWithBasemap}
          mapRef={mapBoxViewerStore.mapRef}
          isHidden={isHidden}
        />
      </div>
    )
  }

  private renderGlobeTileset(tile: ITileset, idx: number, isHidden: boolean) {
    return (
      <div key={'globe-tileset-' + idx}>
        <GlobeViewTileset
          idx={idx}
          tile={tile}
          selectedTilesetFeatureId={
            this.props.mapBoxViewerStore.tilesetsControlStore
              ?.selectedTilesetFeature?.id
          }
          isHidden={isHidden}
        />
      </div>
    )
  }

  private renderGlobeRemoteTileset(
    mapboxRemoteTileset: IRemoteTileset,
    idx: number,
    isHidden: boolean,
  ) {
    return (
      <div key={'globe-remote-tileset-' + idx}>
        <GlobeViewRemoteTileset
          mapboxWMSTileset={mapboxRemoteTileset}
          isHidden={isHidden}
        />
      </div>
    )
  }
}
